/**
 * @fileoverview Serviço de autenticação para comunicação com backend.
 * Centraliza todas as chamadas de API relacionadas à autenticação usando cookies httpOnly
 * para maior segurança contra ataques XSS.
 * Inclui funcionalidades de login, cadastro, reset de senha e atualização de perfil.
 *
 * @module AuthService
 * @since 1.0.0
 */

import { apiRequestJson } from '../api/apiService.js';
import authManager from '../utils/mobileAuth.js';

/**
 * Realiza login do usuário.
 * @param {{email: string, password: string}} credentials - Credenciais do usuário
 * @returns {Promise<{user: Object, accessToken: string, refreshToken: string}>}
 */
export const login = async credentials => {
  const result = await apiRequestJson('auth_login', {
    body: credentials,
  });

  // Mobile: salva tokens no localStorage e inicia monitor
  if (authManager.mobile && result.data.token && result.data.refreshToken) {
    authManager.saveTokens(result.data.token, result.data.refreshToken);
    
    // Inicia monitoramento de sessão
    const { default: sessionMonitor } = await import('../utils/sessionMonitor.js');
    sessionMonitor.start();
  }

  return {
    user: result.data.user,
    accessToken: result.data.token || result.data.accessToken,
    refreshToken: result.data.refreshToken,
  };
};

/**
 * Realiza cadastro do usuário.
 * @param {{name: string, email: string, password: string}} userData - Dados do usuário
 * @returns {Promise<{user: Object, accessToken: string, refreshToken: string}>}
 */
export const signUp = async userData => {
  const mappedData = {
    nome: userData.nome,
    email: userData.email,
    password: userData.password,
    logradouro: userData.logradouro,
    numero: userData.numero,
    complemento: userData.complemento,
    cep: userData.cep,
    cidade: userData.cidade,
    estado: userData.estado,
  };

  const result = await apiRequestJson('auth_register', {
    body: mappedData,
  });

  return {
    user: result.data.user,
    accessToken: result.data.token || result.data.accessToken,
    refreshToken: result.data.refreshToken,
  };
};

/**
 * Verifica se o token é válido.
 * @returns {Promise<{user: Object}>}
 */
export const verifyToken = async () => {
  const result = await apiRequestJson('auth_session');
  return { user: result.data.user };
};

/**
 * Renova o token de acesso usando refresh token.
 * @returns {Promise<void>}
 */
export const refreshToken = async () => {
  // Força detecção mobile
  const isMobile = /Mobile|Android|iPhone|iPad/.test(navigator.userAgent);
  
  if (isMobile) {
    const tokens = authManager.getTokens();
    if (!tokens?.refresh) {
      throw new Error('Refresh token não encontrado');
    }
    
    const result = await apiRequestJson('auth_refresh_hybrid', {
      body: { refreshToken: tokens.refresh }
    });
    
    authManager.saveTokens(result.data.token, result.data.refreshToken);
    return result;
  } else {
    return await apiRequestJson('auth_refresh');
  }
};

/**
 * Solicita reset de senha via email.
 * Envia requisição POST para /api/v1/users/password/reset.
 * O link de reset é montado pelo backend usando a origem da requisição.
 * @param {string} email - Email do usuário
 * @returns {Promise<{message: string}>} Mensagem de confirmação
 * @throws {Error} Erro de validação ou comunicação com API
 */
export const requestPasswordReset = async email => {
  const resetMessage =
    'Você solicitou a redefinição de sua senha. Clique no link abaixo para continuar.';

  const result = await apiRequestJson('users_password_reset', {
    body: {
      email,
      message: resetMessage,
    },
  });

  return {
    message: result.message || 'Email de reset enviado com sucesso',
  };
};

/**
 * Redefine a senha usando token de reset.
 * @param {{token: string, password: string}} data - Token e nova senha
 * @returns {Promise<{message: string}>}
 */
export const resetPassword = async data => {
  const result = await apiRequestJson('users_password_update', {
    body: data,
  });

  return {
    message: result.message || 'Senha atualizada com sucesso',
  };
};

/**
 * Atualiza perfil do usuário.
 * @param {Object} userData - Dados do usuário
 * @returns {Promise<{user: Object}>}
 */
export const updateProfile = async userData => {
  const user = JSON.parse(localStorage.getItem('user'));
  if (!user?.id) {
    throw new Error('Usuário não encontrado');
  }

  const result = await apiRequestJson('users_update', {
    params: { id: user.id },
    body: userData,
  });

  return {
    user: result.data.user,
  };
};

/**
 * Realiza logout do usuário.
 * @returns {Promise<void>}
 */
export const logout = async () => {
  await apiRequestJson('auth_logout');
  
  // Mobile: para monitor e limpa todos os dados
  if (authManager.mobile) {
    const { default: sessionMonitor } = await import('../utils/sessionMonitor.js');
    sessionMonitor.stop();
    
    authManager.clearTokens();
    const { default: dataManager } = await import('../utils/mobileDataManager.js');
    dataManager.clearAll();
  }
};
